<template>
  <aside
    class="app-sidebar"
    :class="[
      { 'app-sidebar--minimized': isMinimized },
      { 'app-sidebar--open': !isMinimized },
    ]"
    @dblclick="toggleMinimized"
  >
    <div
      class="flex flex-row items-center"
      :class="{
        'px-5 justify-between': !isMinimized,
        'justify-center': isMinimized,
      }"
    >
      <LayoutBrandLogo
        id="branded-logo"
        class="h-9 max-w-[186px] w-full"
        :class="isMinimized ? 'app-sidebar-logo-default' : ''"
        :isMinimized="isMinimized"
      />
      <button
        @click="toggleMinimized"
        class="flex app-sidebar-toggle-icon justify-center items-center text-primary"
        :class="{ 'app-sidebar-toggle-icon-minimized': isMinimized }"
      >
        <span class="material-symbols-outlined">
          {{ isMinimized ? 'left_panel_open' : 'left_panel_close' }}
        </span>
      </button>
    </div>

    <!-- Render a NavbarSearch unless we're on restricted pages -->
    <NavbarSearchNew
      v-if="!pagesWithoutSearch.includes(currentPage)"
      name="term"
      class="app-sidebar__nav mt-1 mb-2"
    />

    <div class="app-sidebar__wrapper">
      <div class="flex flex-col justify-center">
        <!-- back to main menu button when settings drawer is open -->
        <BaseButton
          no-padding
          v-if="showSettings"
          @click="showSettings = false"
          :type="'text'"
          :symbol="'chevron_left'"
          :label="$t('Back to main menu')"
          class="m-4 app-sidebar__nav--text"
          :style="[{ 'justify-content': 'start' }]"
        />
        <BaseButton
          no-padding
          v-if="!showSettings && route.path.includes('user-settings')"
          @click="showSettings = true"
          :type="'text'"
          :symbol="'chevron_left'"
          :label="$t('Settings')"
          class="m-4 app-sidebar__nav--text"
          :style="[{ 'justify-content': 'start' }]"
        />

        <!-- Account Settings Navigation -->
        <LayoutSettingsNew :isMinimized="isMinimized" v-if="showSettings" />

        <!-- Main Menu Navigation -->
        <nav class="app-sidebar__nav" v-else>
          <ul class="flex flex-col">
            <li
              class="app-sidebar__drawer__menu__item"
              v-for="(navbarItem, index) in navbarItems"
              :key="index"
            >
              <!--todo make icons rounded instead of square-->
              <NuxtLink
                v-if="!navbarItem.children"
                :to="localePath(navbarItem.path)"
                class="app-sidebar__nav__link--main relative"
                :class="{ active: isActive(navbarItem.path) }"
              >
                <!-- nav icon-->
                <span
                  :id="navbarItem.id"
                  class="material-symbols-outlined app-sidebar__nav--icon"
                  >{{ navbarItem.icon }}</span
                >
                <!-- nav text -->
                <span class="app-sidebar__nav--text">
                  {{ $t(navbarItem.name) }}</span
                >
                <!-- Tooltip for minimized sidebar -->
                <div v-if="isMinimized" class="app-sidebar__tooltip">
                  <span class="app-sidebar__tooltip__badge">
                    {{ $t(navbarItem.name) }}
                  </span>
                </div>
              </NuxtLink>
              <!-- Dropdown menu -->
              <NuxtLink
                v-else
                :id="navbarItem.id"
                :to="localePath(navbarItem.path)"
                class="app-sidebar__nav__link--main"
                :class="{ active: isActive(navbarItem.path) }"
              >
                <!-- nav icon-->
                <span
                  class="material-symbols-outlined app-sidebar__nav--icon"
                  >{{ navbarItem.icon }}</span
                >
                <!-- nav text -->
                <span class="app-sidebar__nav--text">
                  {{ $t(navbarItem.name) }}</span
                >
              </NuxtLink>
              <ul
                v-if="navbarItem.children"
                class="app-sidebar__submenu--transition app-sidebar__nav--text"
                :class="{
                  'app-sidebar__submenu--active': isActive(navbarItem.path),
                  'app-sidebar__submenu--inactive': !isActive(navbarItem.path),
                }"
              >
                <li v-for="subItem in navbarItem.children" :key="subItem.name">
                  <!-- 44px -->
                  <NuxtLink
                    :id="subItem.id"
                    @click="
                      handleDropdownClick({
                        path: subItem.path,
                        view: subItem.query?.view,
                      })
                    "
                    class="app-sidebar__nav__link--main"
                    :class="{
                      active: isActiveSub(subItem.path, subItem.query?.view),
                    }"
                  >
                    <!-- nav icon-->
                    <span
                      v-if="subItem.icon"
                      class="material-symbols-outlined app-sidebar__nav--icon"
                      >{{ subItem.icon }}</span
                    >
                    <!-- nav text -->
                    <span class="app-sidebar__nav--text">
                      {{ $t(subItem.name) }}</span
                    >
                  </NuxtLink>
                </li>
              </ul>
              <!-- Tooltip for minimized sidebar -->
              <div v-if="isMinimized" class="app-sidebar__tooltip">
                <div
                  class="app-sidebar__tooltip__badge"
                  :class="{
                    'app-sidebar__tooltip__badge--children':
                      navbarItem.children,
                  }"
                >
                  <span
                    class="app-sidebar__tooltip__section_name"
                    :class="{ section_name: navbarItem.children }"
                    >{{ $t(navbarItem.name) }}</span
                  >
                  <hr
                    v-if="navbarItem.children"
                    class="w-full border-border-secondary mb-1"
                  />
                  <template v-if="navbarItem.children">
                    <div
                      v-for="child in navbarItem.children"
                      :key="child.name"
                      class="app-sidebar__tooltip__child"
                      :class="{
                        'app-sidebar__tooltip__child--active': isActiveSub(
                          child.path,
                          child.query?.view
                        ),
                      }"
                      @click="
                        handleDropdownClick({
                          path: child.path,
                          view: child.query?.view,
                        })
                      "
                    >
                      {{ $t(child.name) }}
                    </div>
                  </template>
                </div>
              </div>
            </li>
          </ul>
        </nav>
      </div>

      <!-- User: loggedin -->
      <nav v-if="userProfile" class="app-sidebar__nav mt-auto">
        <ul class="flex flex-col justify-center">
          <li
            v-if="$features?.notifications"
            class="app-sidebar__drawer__menu__item"
            :key="5"
          >
            <div
              class="app-sidebar__nav__link--main"
              ref="bellContainerRef"
              :class="{ active: isActive('/notifications') }"
            >
              <!-- the w-full here is a workaround to allow proper centering both when minimized full view -->
              <button
                tabindex="0"
                class="flex flex-row items-center gap-2"
                :class="{ 'w-full': !isMinimized }"
                @click="bellIconClicked()"
                @dblclick="bellDblClick"
              >
                <!-- Tooltip for minimized sidebar -->
                <div
                  v-if="isMinimized && !notificationsDropdownOpen"
                  class="app-sidebar__tooltip"
                >
                  <span class="app-sidebar__tooltip__badge">
                    {{ $t('Notifications') }}
                  </span>
                </div>
                <span class="material-symbols-outlined app-sidebar__nav--icon"
                  >notifications</span
                >
                <!-- nav text -->
                <span class="app-sidebar__nav--text">{{
                  $t('Notifications')
                }}</span>
                <!-- Notification count-->
                <span
                  v-show="notificationStore?.count > 0 && !isMinimized"
                  class="border border-primary rounded-full text-sm px-2.5 py-0.5 ml-auto"
                  >{{ notificationStore?.count }}</span
                >
              </button>
              <LayoutNotifications
                v-if="notificationsDropdownOpen"
                class="absolute bottom-0 left-[300px]"
                :class="{ 'left-[60px]': isMinimized }"
                @close="notificationsDropdownOpen = false"
              />
            </div>
          </li>
          <li class="app-sidebar__drawer__menu__item" :key="6">
            <NuxtLink
              :to="localePath('/activity/all')"
              class="app-sidebar__nav__link--main relative"
              ref="activityContainerRef"
              :id="'onboarding-desk2'"
            >
              <!-- nav icon-->
              <span class="material-symbols-outlined app-sidebar__nav--icon"
                >play_circle</span
              >
              <!-- nav text -->
              <span class="app-sidebar__nav--text">{{
                $t('Processes & Activity')
              }}</span>
              <!-- Tooltip for minimized sidebar -->
              <div v-if="isMinimized" class="app-sidebar__tooltip">
                <div
                  class="app-sidebar__tooltip__badge app-sidebar__tooltip__badge--children"
                >
                  <span class="app-sidebar__tooltip__section_name">{{
                    $t('Processes & Activity')
                  }}</span>
                  <hr class="w-full border-border-secondary mb-1" />
                  <div
                    v-for="item in fullActivityItems"
                    :key="item.name"
                    class="app-sidebar__tooltip__child"
                    :class="{
                      'app-sidebar__tooltip__child--active': isActive(
                        item.path
                      ),
                    }"
                    @click="$router.push(localePath(item.path))"
                  >
                    {{ $t(item.name) }}
                  </div>
                </div>
              </div>
            </NuxtLink>
            <!-- Submenu for expanded sidebar -->
            <ul
              class="app-sidebar__submenu--transition app-sidebar__nav--text"
              :class="{
                'app-sidebar__submenu--active': isActivitySubmenuActive,
              }"
            >
              <li v-for="item in fullActivityItems" :key="item.name">
                <NuxtLink
                  :to="localePath(item.path)"
                  class="app-sidebar__nav__link--main"
                  :class="{ active: isActive(item.path) }"
                >
                  <span class="app-sidebar__nav--text">{{
                    $t(item.name)
                  }}</span>
                </NuxtLink>
              </li>
            </ul>
            <!-- Tooltip for minimized sidebar (fallback outside link) -->
            <div v-if="isMinimized" class="app-sidebar__tooltip">
              <div
                class="app-sidebar__tooltip__badge app-sidebar__tooltip__badge--children"
              >
                <span class="app-sidebar__tooltip__section_name">{{
                  $t('Processes & Activity')
                }}</span>
                <hr class="w-full border-border-secondary mb-1" />
                <div
                  v-for="item in fullActivityItems"
                  :key="item.name"
                  class="app-sidebar__tooltip__child"
                  :class="{
                    'app-sidebar__tooltip__child--active': isActive(item.path),
                  }"
                  @click="$router.push(localePath(item.path))"
                >
                  {{ $t(item.name) }}
                </div>
              </div>
            </div>
          </li>
          <li class="app-sidebar__drawer__menu__item" :key="7">
            <div class="app-sidebar__nav__link--main relative">
              <div v-if="isMinimized" class="app-sidebar__tooltip">
                <span class="app-sidebar__tooltip__badge">
                  {{ $t('Settings') }}
                </span>
              </div>

              <NuxtLink
                :to="
                  localePath({
                    name: 'user-settings',
                    query: {
                      view: 'personal',
                    },
                  })
                "
                class="flex items-center gap-2"
              >
                <span class="material-symbols-outlined app-sidebar__nav--icon"
                  >settings</span
                >
                <span class="app-sidebar__nav--text">{{ $t('Settings') }}</span>
              </NuxtLink>
            </div>
          </li>
        </ul>
      </nav>
      <div class="w-full overflow-hidden cta-container">
        <div class="app-sidebar__nav empty-hidden min-w-[294px]">
          <DashboardCallToAction v-if="!isMinimized" location="user-menu" />
        </div>
      </div>
      <nav v-if="userProfile" class="app-sidebar__nav">
        <ul class="flex flex-col">
          <li
            class="app-sidebar__drawer__menu__item place-self-center w-full"
            :key="8"
          >
            <div
              class="dropdown dropdown-right dropdown-end app-sidebar__nav__link--secondary relative"
              ref="settingsContainerRef"
              :id="'onboarding-desk3'"
            >
              <button
                class="app-sidebar__account_menu__item"
                tabindex="0"
                @click="settingsDropdownOpen = !settingsDropdownOpen"
              >
                <div class="app-sidebar__avatar">
                  {{ getUserInitials }}
                </div>
                <div class="app-sidebar__user-details max-w-[164px]">
                  <span
                    class="text-primary font-semibold text-[14px] truncate text-left"
                    >{{ userProfile.Name }}</span
                  >
                  <span
                    class="text-text-secondary text-[14px] truncate text-left"
                    >{{ userProfile.Email }}</span
                  >
                </div>
                <span
                  class="material-symbols-outlined text-text"
                  v-show="!isMinimized"
                >
                  keyboard_arrow_down
                </span>
              </button>
              <ul
                v-if="settingsDropdownOpen"
                tabindex="0"
                class="app-sidebar__dropdown__menu absolute bottom-0 bg-white w-fit"
                :style="{ left: isMinimized ? '60px' : '270px' }"
              >
                <li
                  class="app-sidebar__dropdown__item app-sidebar__dropdown__item__title"
                >
                  <span class="app-sidebar__dropdown__item__title__text">
                    {{ $t('Signed in as') }}
                    <strong> {{ userProfile.Email }}</strong></span
                  >
                </li>
                <li
                  class="app-sidebar__dropdown__item app-sidebar__dropdown__item--collapse"
                >
                  <input type="checkbox" />
                  <div class="collapse-title">
                    <i class="mdi mdi-earth"></i>
                    <span>{{ $t('Language') }}</span>
                  </div>
                  <div class="collapse-content w-full place-content-center">
                    <ul class="overflow-y-scroll max-h-[40dvh] w-full">
                      <li
                        v-for="(locale, index) in availableLocales"
                        :key="index"
                        :class="[
                          'app-sidebar__item focus:bg-transparent',
                          {
                            'bg-background-tertiary':
                              locale.code === currentLocale,
                          },
                        ]"
                      >
                        <span @click="setAndStoreLocale(locale.code)">
                          <nuxt-img
                            class="h-5 w-5"
                            loading="lazy"
                            :src="`/images/flags/${locale.label.toLowerCase()}.svg`"
                          />
                          <span class="is-uppercase">{{ locale.label }}</span>
                        </span>
                      </li>
                    </ul>
                  </div>
                </li>
                <hr class="w-full border-border-secondary my-1" />
                <li class="app-sidebar__dropdown__item">
                  <NuxtLink to="/invitations">
                    <i class="mdi mdi-email"></i>
                    <span>{{ $t('Invitations') }}</span>
                  </NuxtLink>
                </li>
                <hr class="w-full border-border-secondary my-1" />
                <li class="app-sidebar__dropdown__item">
                  <NuxtLink
                    :to="
                      localePath({
                        name: 'changelog',
                      })
                    "
                  >
                    <i class="mdi mdi-creation"></i>
                    <span>{{ $t('Whats new') }}</span>
                  </NuxtLink>
                </li>
                <li class="app-sidebar__dropdown__item">
                  <button @click="showPrivacySettings">
                    <i class="mdi mdi-cookie"></i>
                    <span>{{ $t('Privacy Settings') }}</span>
                  </button>
                </li>
                <li class="app-sidebar__dropdown__item">
                  <button @click="helpModal">
                    <i class="mdi mdi-help"></i>
                    <span>{{ $t('Help') }}</span>
                  </button>
                </li>
                <li class="app-sidebar__dropdown__item">
                  <NuxtLink to="https://legal.transkribus.org/" target="_blank">
                    <i class="mdi mdi-scale-balance"></i>
                    <span>{{ $t('Legal Information') }}</span>
                  </NuxtLink>
                </li>
                <li
                  v-if="userProfile?.IsAdmin"
                  class="app-sidebar__dropdown__item"
                >
                  <a @click="togglePosthog()">
                    <i
                      :class="
                        posthogEnabled
                          ? 'mdi mdi-eye-outline'
                          : 'mdi mdi-eye-off-outline'
                      "
                      class="app-sidebar__dropdown__icon"
                      aria-hidden="true"
                    ></i>
                    <span v-if="posthogEnabled">{{
                      $t('Disable posthog')
                    }}</span>
                    <span v-else>{{ $t('Enable posthog') }}</span>
                  </a>
                </li>
                <hr class="w-full border-border-secondary my-1" />
                <li class="app-sidebar__dropdown__item">
                  <button @click="logout">
                    <i class="mdi mdi-logout"></i>
                    <span>{{ $t('Logout') }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import HelpModal from '~/components/Modals/Help.vue'
const { $bus, $posthog, $features, $sentry }: any = useNuxtApp()
const { locale: currentLocale, setLocale, t } = useI18n()
const i18n = useI18n()
const availableLocales = i18n.locales
const localePath = useLocalePath()
const route = useRoute()
const configStore = useConfig()
const userApi = useUser()
const { userProfile, keycloak } = useKeycloak()
const router = useRouter()
const notificationsApi = useNotifications()

const { hermesService } = useHermesService()
const { hasAccess: hasHermesAccess } = useHermesAccess()

const bellContainerRef = ref<HTMLElement | null>(null)
const activityContainerRef = ref<HTMLElement | null>(null)
const settingsContainerRef = ref<HTMLElement | null>(null)
const notificationStore = useNotificationsStore()
const showSettings = ref(false)
const posthogEnabled = ref(!$posthog.has_opted_out_capturing()) // Initialize with current state
const notificationsDropdownOpen = ref<boolean>(false)
const settingsDropdownOpen = ref<boolean>(false)
const activityDropdownOpen = ref<boolean>(false)

/**
 * PAGES that should NOT show the NavbarSearch.
 */
const pagesWithoutSearch = [
  '/sites',
  '/models',
  'my-models',
  '/data-lab',
  '/training',
  '/training/gallery',
  '/datasets',
]

onMounted(async () => {
  // if the route includes user-settings, switch the drawer
  if (route.path.includes('user-settings')) {
    showSettings.value = true
  } else {
    showSettings.value = false
  }
})

// watcher for route changes
watch(
  () => route.path,
  () => {
    if (route.path.includes('user-settings')) {
      showSettings.value = true
    } else {
      showSettings.value = false
    }
  }
)

const showQualityControl = computed(() => {
  return (
    $features?.qc &&
    (userProfile.Email.endsWith('@transkribus.org') ||
      userProfile.Email.endsWith('@readcoop.eu'))
  )
})

const currentPage = computed(() => {
  if (route.path.includes('collection')) return '/collection/overview'
  if (route.path.includes('training')) return '/training'
  if (route.path.includes('models')) return '/models'
  if (route.path.includes('my-models')) return '/my-models'
  if (route.path.includes('data-lab')) return '/models'
  if (route.path.includes('datasets')) return '/datasets'
  if (route.path.includes('sites')) return '/sites'
  if (route.path.includes('learn')) return '/learn'
  if (route.path.includes('quality-control')) return '/quality-control'
  if (
    route.path.includes('user-settings') ||
    route.path.includes('invitations') ||
    route.path.includes('changelog')
  )
    return '/user-settings'
  if (route.path.includes('activity')) return '/activity/all'
  if (route.path.includes('notifications')) return '/notifications'
  if (route.path.includes('projects')) return '/projects'
  else return '/'
})

const navbarItems = computed(() => {
  const baseItems = [
    {
      name: 'Home',
      path: '/',
      icon: 'home',
      id: 'onboarding-desk4',
    },
    {
      name: 'Desk',
      // path: '/collection/overview',
      path: '/collection',
      icon: 'grid_view',
      id: 'onboarding-desk1',
      // children: [
      //   {
      //     name: 'Overview',
      //     path: '/collection/overview',
      //   },
      //   {
      //     name: 'Collections',
      //     path: '/collection',
      //   },
      // ],
    },
    {
      name: 'Models',
      path: '/models',
      icon: 'model_training',
      id: '',
      children: [
        {
          name: 'Public models',
          path: '/models',
        },
        {
          name: 'My models',
          path: '/my-models',
        },
        {
          name: 'Training lab',
          path: '/data-lab',
        },
      ],
    },
    {
      name: 'Sites',
      path: '/sites',
      icon: 'web',
      id: '',
      children: [
        {
          name: 'My sites',
          path: '/sites',
          query: { view: 'private' },
        },
        {
          name: 'Public sites',
          path: '/sites',
          query: { view: 'public' },
        },
        {
          name: 'World map',
          path: '/sites/map',
        },
      ],
    },
  ]

  baseItems[1].path = '/collection/overview'
  baseItems[1].children = [
    {
      name: 'Overview',
      path: '/collection/overview',
    },
    {
      name: 'Collections',
      path: '/collection',
    },
    ...(showQualityControl.value
      ? [{ name: 'Quality control', path: '/quality-control' }]
      : []),
  ]
  if (userProfile.IsAdmin) {
    baseItems[3].children.push({
      name: 'Admin',
      path: '/sites',
      query: { view: 'admin' },
    })
  }
  if ($features.dataSets) {
    baseItems.push({ name: 'Datasets', path: '/datasets', icon: 'data_table' })
  }
  if ($features.projects) {
    baseItems.push({ name: 'Projects', path: '/projects', icon: 'folder' })
  }
  return baseItems
})

const activityItems = computed(() => {
  return [
    {
      name: 'Recognition runs',
      path: '/activity/recognition',
      icon: 'receipt',
    },
    {
      name: 'Training runs',
      path: '/data-lab',
      icon: 'receipt',
    },
    {
      name: 'Uploads & downloads',
      path: '/activity/up-download',
      icon: 'receipt',
    },
  ]
})

const fullActivityItems = computed(() => {
  const items = [...activityItems.value]
  items.push({ name: 'Other Processes', path: '/activity/advanced' })
  if (hasHermesAccess.value) {
    items.push({ name: 'Hermes', path: '/activity/hermes' })
  }
  if (userProfile?.IsAdmin) {
    items.push({ name: 'All Jobs', path: '/activity/all' })
  }
  return items
})

const isActivitySubmenuActive = computed(() => {
  return (
    isActive('/activity/all') ||
    fullActivityItems.value.some(i => isActive(i.path))
  )
})

const helpModal = () => {
  const modal = {
    modalContent: {
      component: HelpModal,
    },
  }
  $bus.emit('open-modal', { modal: modal, props: { isMedium: true } })
}

watch(posthogEnabled, newValue => {
  if (newValue) {
    $posthog.opt_in_capturing()
  } else {
    $posthog.opt_out_capturing()
  }
})

const bellDblClick = async () => {
  await notificationsApi.testNotification()
}

const containsTarget = (refEl: unknown, node: Node) => {
  const el = refEl instanceof HTMLElement ? refEl : (refEl as any)?.$el
  return !!el?.contains?.(node)
}

const handleClickOutside = (event: MouseEvent) => {
  // Check if the click target is outside both the bell icon and dropdown
  const target = event.target as Node

  // Check if click is inside bell container
  const isClickInsideBell = containsTarget(bellContainerRef.value, target)
  const isClickInsideActivity = containsTarget(
    activityContainerRef.value,
    target
  )
  const isClickInsideSettings = containsTarget(
    settingsContainerRef.value,
    target
  )

  // Only close if clicking outside both elements
  if (!isClickInsideBell) {
    notificationsDropdownOpen.value = false
  }
  if (!isClickInsideActivity) {
    activityDropdownOpen.value = false
  }
  if (!isClickInsideSettings) {
    settingsDropdownOpen.value = false
  }
}

const bellIconClicked = async () => {
  if (notificationsDropdownOpen.value) {
    notificationsDropdownOpen.value = false
    return
  }
  notificationsDropdownOpen.value = true
}

function logout() {
  // Clear hermes cookies to prevent token mismatch when switching users
  hermesService.clearTokens()

  // Proceed with normal logout
  keycloak?.logout()
  $posthog.reset()
}

function togglePosthog() {
  posthogEnabled.value = !posthogEnabled.value // Toggle the state
}

const showPrivacySettings = () => {
  if ((window as any).UC_UI) {
    ;(window as any).UC_UI.showSecondLayer()
  }
}

function setAndStoreLocale(newLocale: string) {
  if (configStore.config == null) return
  configStore.config.locale = newLocale
  setLocale(newLocale)
  userApi.updateUserConfig({
    userId: userProfile?.UserId,
    config: { locale: newLocale },
  })
}

function handleDropdownClick(options: { path: string; view?: string }) {
  if (options.view) {
    router.push({
      path: options.path,
      query: { view: options.view },
    })
  } else {
    router.push({
      path: options.path,
    })
  }
}

function isActive(path: string): boolean {
  // console.log('🚀 ~ isActive ~ path:', path)
  // console.log('🚀 ~ isActive ~ currentPage:', currentPage.value)
  return path === currentPage.value
}

function isActiveSub(path: string, query: string): boolean {
  // Special handling for collection overview and collection pages
  if (path === '/collection/overview') {
    return route.path === '/collection/overview'
  }
  if (path === '/collection') {
    return (
      route.path.includes('/collection') &&
      route.path !== '/collection/overview'
    )
  }

  return route.path.includes(path) && route.query.view === query
}

const getUserInitials = computed(() => {
  if (!userProfile?.FirstName && !userProfile?.LastName) return ''

  if (!userProfile.FirstName) {
    return userProfile?.LastName?.[0].toUpperCase()
  } else if (!userProfile.LastName) {
    return userProfile?.FirstName?.[0].toUpperCase()
  } else {
    return (
      userProfile?.FirstName?.[0] + userProfile?.LastName?.[0]
    ).toUpperCase()
  }
})

const isMinimized = ref<boolean>(false)

function toggleMinimized() {
  isMinimized.value = !isMinimized.value
  localStorage.setItem('sidebarMinimized', isMinimized.value ? 'true' : 'false')
  $bus.emit('isMinimised')
}

function handleResize() {
  const windowWidth = window.innerWidth
  const breakpoint = 1024 // lg breakpoint

  // If window width is less than breakpoint, minimize the sidebar
  if (windowWidth < breakpoint) {
    isMinimized.value = true
  } else {
    // If window width is greater than breakpoint, restore from localStorage preference
    const savedState = localStorage.getItem('sidebarMinimized')
    isMinimized.value = savedState === 'true'
  }
}

onMounted(() => {
  const savedState = localStorage.getItem('sidebarMinimized')
  if (savedState === 'true') {
    isMinimized.value = true
  }

  if (userProfile?.Email) {
    $sentry.setUser({
      email: userProfile.Email,
    })
  }

  handleResize()
  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleClickOutside)
})
</script>
